import { forwardRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@bizzabo/design-system';
import { UserPreview } from '@bizzabo/entities';

import UserInfoContainer from './UserInfoContainer';
import UserInfoSkeleton from './UserInfoSkeleton';
import UserInfoView from './UserInfoView';

export interface UserBio {
    user: UserPreview;
    closePopup?: () => void;
    position?: {
        x: number;
        y: number;
    };
    onButtonClick?: () => void;
    isLoading?: boolean;
    isUserModerator?: boolean;
    onLinkedInClick?: () => void;
    onTwitterClick?: () => void;
}

type StyleProps = {
    x: number;
    y: number;
};

const UserInfo = forwardRef<HTMLDivElement, UserBio>(
    (
        {
            user,
            closePopup = () => {},
            position = {},
            onButtonClick,
            isLoading = false,
            isUserModerator = false,
            onLinkedInClick = () => {},
            onTwitterClick = () => {},
        },
        ref
    ) => {
        const classes = useStyles({ x: position.x, y: position.y });

        if (isLoading) {
            return (
                <UserInfoContainer containerClass={classes.container} ref={ref}>
                    <UserInfoSkeleton />
                </UserInfoContainer>
            );
        }

        if (!(position.x >= 0 && position.y >= 0 && user?.userProfileId)) {
            return null;
        }

        return (
            <UserInfoContainer containerClass={classes.container} ref={ref}>
                <UserInfoView
                    user={user}
                    isUserModerator={isUserModerator}
                    onClose={closePopup}
                    onButtonClick={onButtonClick}
                    onLinkedInClick={onLinkedInClick}
                    onTwitterClick={onTwitterClick}
                />
            </UserInfoContainer>
        );
    }
);

UserInfo.displayName = 'UserInfo';

const useStyles = makeStyles<Theme, StyleProps>(
    () => ({
        container: {
            zIndex: 1001,
            left: ({ x }) => x,
            top: ({ y }) => y,
            position: 'absolute',
            transition: 'left 200ms ease-in, top 200ms ease-in, background-color 300ms ease-in-out',
        },
    }),
    { classNamePrefix: 'people-user-info' }
);

export default UserInfo;
